import {
  isWorkbookValueListOperator,
  type WorkbookColumnarSheet,
  type WorkbookDefinition,
} from '@erp/shared-types';
import { ArrowSquareOutIcon, CaretRightIcon } from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import { formulaText, matchSheetKind } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { hasColumnFormula } from './worksheetColumns';
import { filterTakesValue } from './worksheetFilterOperators';
import { useFilterValuesText } from './WorksheetFilters';
import type { WorkbookReferenceLabels } from './useWorkbookReferences';
import { WorksheetFormula } from './WorksheetFormula';
import {
  formulaTables,
  worksheetFormulaFields,
} from './worksheetFormulaFields';
import { workbookItemIconKind } from './workbookItemIconKind';
import { PivotProcessDetails } from './pivot/PivotProcessDetails';
import { FreeformProcessDetails } from './freeform/FreeformProcessDetails';
import {
  useSortDirectionLabel,
  worksheetFieldType,
} from './worksheetTypeLabels';

export function WorkbookStepConfiguration({
  definition,
  references,
  itemIds,
  openItem,
  summaryOnly = false,
}: {
  definition: WorkbookDefinition;
  references: WorkbookReferenceLabels;
  itemIds: string[];
  summaryOnly?: boolean;
  openItem: (id: string) => void;
}) {
  return (
    <div className="mt-2 space-y-2">
      {itemIds.map((id) => {
        const sheet = definition.sheets.find((item) => item.id === id);
        return (
          <div key={id} className="space-y-2">
            {!summaryOnly && (
              <Button size="sm" variant="ghost" onClick={() => openItem(id)}>
                <ArrowSquareOutIcon />
                {references.name(id)}
              </Button>
            )}
            {sheet &&
              matchSheetKind(sheet, {
                columnar: (columnar) => (
                  <WorksheetProcessDetails
                    sheet={columnar}
                    definition={definition}
                    references={references}
                  />
                ),
                pivot: (pivot) => <PivotProcessDetails sheet={pivot} />,
                freeform: (freeform) => (
                  <FreeformProcessDetails sheet={freeform} />
                ),
              })}
          </div>
        );
      })}
    </div>
  );
}

function WorksheetProcessDetails({
  sheet,
  definition,
  references,
}: {
  sheet: WorkbookColumnarSheet;
  definition: WorkbookDefinition;
  references: WorkbookReferenceLabels;
}) {
  const gt = useGT();
  const sortLabel = useSortDirectionLabel();
  const valuesText = useFilterValuesText();
  const operators = {
    eq: gt('equals', { context: 'workbook process filter operator' }),
    neq: gt('does not equal', { context: 'workbook process filter operator' }),
    gt: gt('is greater than', { context: 'workbook process filter operator' }),
    gte: gt('is greater than or equal to', {
      context: 'workbook process filter operator',
    }),
    lt: gt('is less than', { context: 'workbook process filter operator' }),
    lte: gt('is less than or equal to', {
      context: 'workbook process filter operator',
    }),
    contains: gt('contains', { context: 'workbook process filter operator' }),
    'is-null': gt('is empty', { context: 'workbook process filter operator' }),
    'is-not-null': gt('is not empty', {
      context: 'workbook process filter operator',
    }),
    'is-error': gt('is an error', {
      context: 'workbook process filter operator',
    }),
    'is-not-error': gt('is not an error', {
      context: 'workbook process filter operator',
    }),
    'is-na': gt('is #N/A', { context: 'workbook process filter operator' }),
    'is-one-of': gt('is one of', {
      context: 'workbook process filter operator',
    }),
    'is-not-one-of': gt('is not one of', {
      context: 'workbook process filter operator',
    }),
  };
  return (
    <>
      {sheet.filters.map((filter, index) => (
        <p key={index} className="text-sm text-muted-foreground">
          <T context="workbook process filter summary">
            Keep rows where <Var>{filter.column}</Var>{' '}
            <Var>{operators[filter.operator]}</Var>{' '}
            <Var>
              {isWorkbookValueListOperator(filter.operator)
                ? valuesText(filter)
                : !filterTakesValue(filter.operator)
                  ? ''
                  : filter.value ||
                    gt('(blank)', { context: 'workbook process empty value' })}
            </Var>
            .
          </T>
        </p>
      ))}
      {sheet.columns.map(
        (column, index) =>
          hasColumnFormula(column) && (
            <p
              key={column.name}
              className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
            >
              <span>{column.name}</span>
              <WorksheetFormula
                expression={column.expression}
                fields={worksheetFormulaFields({
                  sheet,
                  inputName: references.name(sheet.inputId),
                  inputIcon:
                    workbookItemIconKind(definition, sheet.inputId) ?? 'data',
                  itemName: (id) => references.name(id),
                  itemIcon: (id) => workbookItemIconKind(definition, id),
                  columnIndex: index,
                  tables: formulaTables(definition, undefined, sheet.id),
                })}
              />
            </p>
          ),
      )}
      {sheet.sort.map((sort, index) => {
        const direction = sortLabel(
          worksheetFieldType(sheet, sort.column),
          sort.direction,
        );
        return (
          <p key={sort.column} className="text-sm text-muted-foreground">
            {index === 0
              ? gt('First sort by {column}, {direction}.', {
                  context: 'workbook process ordered sort summary',
                  column: sort.column,
                  direction,
                })
              : gt(
                  'Then sort by {column}, {direction}, within matching values above.',
                  {
                    context: 'workbook process ordered sort summary',
                    column: sort.column,
                    direction,
                  },
                )}
          </p>
        );
      })}
      <Collapsible className="text-xs">
        <CollapsibleTrigger className="group flex items-center gap-1 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring">
          <CaretRightIcon className="size-3.5 transition-transform group-data-[panel-open]:rotate-90" />
          <T context="workbook process calculation disclosure">
            Calculation details
          </T>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-2 border-l pl-3">
          {sheet.lookups.map((lookup, index) => (
            <p key={index}>
              <T context="workbook process lookup summary">
                Look up <Var>{lookup.valueColumn}</Var> in{' '}
                <Var>{references.name(lookup.sourceId)}</Var> by matching{' '}
                <Var>{lookup.inputColumn}</Var> to{' '}
                <Var>{lookup.matchColumn}</Var>.
              </T>
            </p>
          ))}
          <p className="text-muted-foreground">
            <T context="workbook process formula execution">
              Calculate each formula after the columns it uses, then filter and
              sort.
            </T>
          </p>
          {sheet.columns.map((column) => (
            <p key={column.name}>
              {column.name}: <code>{formulaText(column.expression)}</code>
            </p>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}
